import { Calendar } from '@/components/ui/shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatDateForDisplay } from '@/lib/utils';
import { differenceInDays, format } from 'date-fns';
import { Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface InlineDateCellProps {
    value?: string | null;
    status?: string | null;
    completedAt?: string | null;
    updatedAt?: string | null;
    onUpdate?: (value: string | null) => void;
    placeholder?: string;
    className?: string;
    readOnly?: boolean;
}

export function InlineDateCell({
    value,
    status,
    completedAt,
    updatedAt,
    onUpdate,
    placeholder = 'Set date...',
    className,
    readOnly = false,
}: InlineDateCellProps) {
    const { t } = useTranslations();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [popoverSide, setPopoverSide] = useState<'bottom' | 'left'>('bottom');
    const [popoverAlign, setPopoverAlign] = useState<'start' | 'center' | 'end'>('end');
    const [popoverAlignOffset, setPopoverAlignOffset] = useState(0);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value ? new Date(value) : undefined);

    const statusKey = (status ?? '').toLowerCase();
    const isCompleted = statusKey === 'completed';
    const isCancelled = statusKey === 'canceled' || statusKey === 'cancelled';

    const terminalDate = useMemo(() => {
        const candidate = isCompleted ? completedAt : isCancelled ? updatedAt : null;
        if (!candidate) return null;

        const parsed = new Date(candidate);
        if (Number.isNaN(parsed.getTime())) return null;

        parsed.setHours(0, 0, 0, 0);
        return parsed;
    }, [completedAt, isCancelled, isCompleted, updatedAt]);

    // Calculate days remaining/overdue OR early/late (if terminal)
    const daysInfo = useMemo(() => {
        if (!value) return null;

        const estimatedDate = new Date(value);
        if (Number.isNaN(estimatedDate.getTime())) return null;
        estimatedDate.setHours(0, 0, 0, 0);

        if (terminalDate) {
            const daysDiff = differenceInDays(terminalDate, estimatedDate); // +late, -early
            return {
                mode: 'terminal' as const,
                days: Math.abs(daysDiff),
                isLate: daysDiff > 0,
                isEarly: daysDiff < 0,
                isOnTime: daysDiff === 0,
            };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

        const daysDiff = differenceInDays(estimatedDate, today);
        return {
            mode: 'live' as const,
            days: Math.abs(daysDiff),
            isOverdue: daysDiff < 0,
            isDueToday: daysDiff === 0,
        };
    }, [terminalDate, value]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) {
            setPopoverSide('bottom');
            setPopoverAlign('end');
            return;
        }

        // Approximate calendar popover width in px (DayPicker is usually ~300-340px wide)
        const expectedWidth = 340;
        const spaceRight = window.innerWidth - rect.right;
        const spaceLeft = rect.left;

        // If we're near the right edge, open to the left so the calendar doesn't get cut off.
        if (spaceRight < expectedWidth && spaceLeft > 140) {
            setPopoverSide('left');
            setPopoverAlign('center');
            setPopoverAlignOffset(0);
            return;
        }

        // Otherwise, keep it below and align the popover's right edge to the trigger's right edge.
        setPopoverSide('bottom');
        setPopoverAlign('end');
        setPopoverAlignOffset(0);
    }, [isOpen]);

    const handleDateSelect = async (date: Date | undefined) => {
        if (!date) {
            return;
        }

        if (!onUpdate) return;

        const formattedDate = format(date, 'yyyy-MM-dd');

        // If date hasn't changed, just close
        if (formattedDate === value) {
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(formattedDate);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to update date:', error);
            // Revert on error
            setSelectedDate(value ? new Date(value) : undefined);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = async () => {
        if (!value) {
            setIsOpen(false);
            return;
        }

        if (!onUpdate) return;

        setIsLoading(true);
        try {
            await onUpdate(null);
            setSelectedDate(undefined);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to clear date:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Read-only mode: just display the date
    if (readOnly || !onUpdate) {
        return (
            <div className={cn('flex w-full flex-col gap-0.5 px-2 py-1.5 text-left text-sm', !value && 'text-muted-foreground', className)}>
                <div className="flex items-center gap-1.5">
                    <CalendarIcon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{value ? formatDateForDisplay(new Date(value)) : placeholder}</span>
                </div>
                {daysInfo && (
                    <div
                        className={cn(
                            'ml-5 text-xs',
                            daysInfo.mode === 'terminal'
                                ? daysInfo.isLate
                                    ? 'text-destructive font-medium'
                                    : daysInfo.isEarly
                                      ? 'text-success-subtle-foreground font-medium'
                                      : 'text-muted-foreground'
                                : daysInfo.isOverdue
                                  ? 'text-destructive font-medium'
                                  : 'text-muted-foreground',
                        )}
                    >
                        {daysInfo.mode === 'terminal'
                            ? daysInfo.isLate
                                ? t('work_orders.date_status.days_late', '{count} day late|{count} days late').replace(
                                      '{count}',
                                      String(daysInfo.days),
                                  )
                                : daysInfo.isEarly
                                  ? t('work_orders.date_status.days_early', '{count} day early|{count} days early').replace(
                                        '{count}',
                                        String(daysInfo.days),
                                    )
                                  : t('work_orders.date_status.on_time', 'On time')
                            : daysInfo.isOverdue
                              ? t('work_orders.date_status.days_overdue', '{count} day overdue|{count} days overdue').replace(
                                    '{count}',
                                    String(daysInfo.days),
                                )
                              : daysInfo.isDueToday
                                ? t('work_orders.date_status.due_today', 'Due today')
                                : t('work_orders.date_status.days_left', '{count} day left|{count} days left').replace(
                                      '{count}',
                                      String(daysInfo.days),
                                  )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    className={cn(
                        'hover:bg-accent w-full rounded px-2 py-1.5 text-left transition-colors',
                        'flex flex-col gap-0.5 text-sm',
                        !value && 'text-muted-foreground',
                        className,
                    )}
                >
                    <div className="flex items-center gap-1.5">
                        {isLoading ? (
                            <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <CalendarIcon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        )}
                        <span className="truncate">
                            {value ? formatDateForDisplay(new Date(value)) : placeholder || t('work_orders.date_status.set_date', 'Set date...')}
                        </span>
                    </div>
                    {daysInfo && (
                        <div
                            className={cn(
                                'ml-5 text-xs',
                                daysInfo.mode === 'terminal'
                                    ? daysInfo.isLate
                                        ? 'text-destructive font-medium'
                                        : daysInfo.isEarly
                                          ? 'text-success-subtle-foreground font-medium'
                                          : 'text-muted-foreground'
                                    : daysInfo.isOverdue
                                      ? 'text-destructive font-medium'
                                      : 'text-muted-foreground',
                            )}
                        >
                            {daysInfo.mode === 'terminal'
                                ? daysInfo.isLate
                                    ? t('work_orders.date_status.days_late', '{count} day late|{count} days late').replace(
                                          '{count}',
                                          String(daysInfo.days),
                                      )
                                    : daysInfo.isEarly
                                      ? t('work_orders.date_status.days_early', '{count} day early|{count} days early').replace(
                                            '{count}',
                                            String(daysInfo.days),
                                        )
                                      : t('work_orders.date_status.on_time', 'On time')
                                : daysInfo.isOverdue
                                  ? t('work_orders.date_status.days_overdue', '{count} day overdue|{count} days overdue').replace(
                                        '{count}',
                                        String(daysInfo.days),
                                    )
                                  : daysInfo.isDueToday
                                    ? t('work_orders.date_status.due_today', 'Due today')
                                    : t('work_orders.date_status.days_left', '{count} day left|{count} days left').replace(
                                          '{count}',
                                          String(daysInfo.days),
                                      )}
                        </div>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto p-0"
                side={popoverSide}
                align={popoverAlign}
                alignOffset={popoverAlignOffset}
                sideOffset={8}
                collisionPadding={12}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-3">
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                            setSelectedDate(date);
                            if (date) {
                                handleDateSelect(date);
                            }
                        }}
                        initialFocus
                    />
                    {value && (
                        <div className="border-t p-2">
                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={isLoading}
                                className="text-muted-foreground hover:text-foreground text-xs transition-colors"
                            >
                                {t('work_orders.date_status.clear_date', 'Clear date')}
                            </button>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
